import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { AlertColor } from '@mui/material';
import { useDrop } from 'react-dnd';
import { minMax } from '../constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import {
  addCalculationField,
  addSourceInfoInCalculationFlow,
  deleteSourceField,
  insertFlowSource,
  replaceSourceInfoInCalculationFlow,
  setIsAggregated,
} from '../../../redux/Calculations/CalculationsActions';
import ShortUniqueId from 'short-unique-id';
import { allowAggregationFlowTypes } from '../constants';
import { isFlowAlreadyPresentInStep, isFlowFilledProperly, willCreateCycle } from '../utils';
import { ICalculationFieldsMissingField } from '../../../redux/Calculations/CurrentCalculationSessionInterface';

interface IStringNumberDropZoneProps {
  children: React.ReactNode;
  activeFlowName: string;
  propKey: string;
  activeFlowType: string;
  showBorder: boolean;
  setShowBorder: any;
  disableDrop: boolean;
  missingFields: ICalculationFieldsMissingField[];
  getMissingFields: () => void;
  // flowData: Record<
  //   string,
  //   {
  //     flowId: string;
  //     flowType: string;
  //     flowName: string;
  //     minimumSourcesForThisFlow: number | undefined;
  //     lengthBeforeAdjustment: number;
  //     lengthOfSourcesForThisFlow: number;
  //   }
  // >;
}
function StringNumberDropZone({
  children,
  activeFlowName,
  propKey,
  activeFlowType,
  showBorder,
  setShowBorder,
  disableDrop,
  missingFields,
  getMissingFields,
}: IStringNumberDropZoneProps) {
  const [alert, setAlert] = useState<{ severity: AlertColor; message: string } | null>(null);
  // const [showBorder, setShowBorder] = useState(false);

  const dispatch = useDispatch();
  const { calculations } = useSelector((state: RootState) => ({
    calculations: state.calculations,
  }));
  const { currentCalculationSession } = calculations.properties[propKey];
  const isDropNotAllowed = (draggedFlowUid: string, activeFlowUid: string): boolean => {
    return draggedFlowUid === activeFlowUid;
  };
  const [{ isOver }, dropRef] = useDrop({
    // calculation --> saved calculation, calculationSelected --> flow in the canvas
    accept: ['card', 'calculationSelected', 'calculation'],
    hover: (item: any, monitor) => {
      if (monitor.isOver({ shallow: true }) && !showBorder) {
        setShowBorder(true);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item: any, monitor) => {
      if (disableDrop) {
        setAlert({
          severity: 'warning',
          message:
            'This calculation is being used in another flow. Please remove the dependency before deleting.',
        });
        return;
      }
      if (!currentCalculationSession) return;
      const activeFlow = currentCalculationSession.activeFlow;
      const activeCondition = currentCalculationSession.activeCondition;
      setShowBorder(false);
      if (!activeFlow) return;
      if (item.type === 'calculation') {
        setAlert({
          severity: 'warning',
          message: 'Using another calculated field as a source feature is coming in future release',
        });
        return;
      }
      if (isDropNotAllowed(item.uid, activeFlow)) {
        setAlert({
          severity: 'warning',
          message: 'Same flow can not be dropped in itself',
        });
        return;
      }
      const activeFlowName = currentCalculationSession.calculationInfo.flows[activeFlow][0].flow;
      if (
        item.type === 'calculationSelected' &&
        willCreateCycle(currentCalculationSession.calculationInfo, item.uid, activeFlow)
      ) {
        setAlert({
          severity: 'warning',
          message: 'This flow will create a cycle in the calculation, please choose another flow',
        });
        return;
      }

      if (
        item.type === 'calculationSelected' &&
        isFlowAlreadyPresentInStep(
          currentCalculationSession.calculationInfo,
          item.uid,
          activeFlow,
          activeCondition ?? 0,
          'source',
        )
      ) {
        const droppedFlowName = currentCalculationSession.flowDisplayNames[item.uid];
        const parentFlowName = currentCalculationSession.flowDisplayNames[activeFlow];
        setAlert({
          severity: 'warning',
          message: `${droppedFlowName} is already present inside ${parentFlowName}.`,
        });
        return;
      }

      if (item.type === 'calculationSelected' && !['log', 'absolute'].includes(activeFlowName)) {
        const isAgggregated = currentCalculationSession.flowAggregations[activeFlow];
        if (isAgggregated === null || isAgggregated === undefined) {
          setAlert({
            severity: 'warning',
            message: 'Please select an aggregation type for this flow before dropping a source',
          });
        }
        const droppedItemAggregationType = item.fieldData.isAggregated;
        if (
          (isAgggregated && !droppedItemAggregationType) ||
          (droppedItemAggregationType && !isAgggregated)
        ) {
          setAlert({
            severity: 'warning',
            message: 'Cannot drop a non-aggregated flow into an aggregated flow and vice versa',
          });
          return;
        }
      }
      if (item.type === 'calculationSelected') {
        if (!isFlowFilledProperly(currentCalculationSession.calculationInfo, item.uid)) {
          setAlert({
            severity: 'warning',
            message: `Please ensure at least one field or flow is present and all conditions are met for flow ${item.name} before dropping.`,
          });
          return;
        }
      }

      if (item.type === 'card') {
        const maxOneSourceAllowedList = minMax[activeFlowType][activeFlowName].max;

        if (activeFlowType === 'Number') {
          if (
            currentCalculationSession.calculationInfo.flows[activeFlow][0].flow !== 'log' &&
            currentCalculationSession.calculationInfo.flows[activeFlow][0].sourceType.filter(
              (item: any) =>
                item === 'field' || item === 'flow' || item === 'integer' || item === 'decimal',
            ).length >= maxOneSourceAllowedList &&
            missingFields.length === 0
          ) {
            setAlert({
              severity: 'warning',
              message: 'Can not add more than one source to this flow',
            });
            return;
          } else if (
            currentCalculationSession.calculationInfo.flows[activeFlow][0].flow === 'log' &&
            currentCalculationSession.calculationInfo.flows[activeFlow][0].sourceType.length > 1 &&
            missingFields.length === 0
          ) {
            setAlert({
              severity: 'warning',
              message: 'Can not add more than one source to this flow',
            });
            return;
          }
        } else if (activeFlowType === 'String') {
          if (
            currentCalculationSession.calculationInfo.flows[activeFlow][0].sourceType.filter(
              (item: any) =>
                item === 'field' ||
                item === 'flow' ||
                (activeFlowName === 'concat' && item === 'text'),
            ).length >= maxOneSourceAllowedList &&
            missingFields.length === 0
          ) {
            setAlert({
              severity: 'warning',
              message: 'Can not add more than one source to this flow',
            });
            return;
          }
        }

        if (
          activeFlowType !== null &&
          ((activeFlowType === 'String' && item.fieldData.dataType !== 'text') ||
            // TODO: verify if item.fieldData.dataType is correct is "date" or "datetime" or "timestamp" or how is it represented basically
            (activeFlowType === 'Date' && item.fieldData.dataType !== 'date') ||
            (activeFlowType === 'Number' &&
              item.fieldData.dataType !== 'integer' &&
              item.fieldData.dataType !== 'decimal'))
        ) {
          setAlert({
            severity: 'warning',
            message: 'Incorrect data type for calculation',
          });
          return;
        }

        const uId = new ShortUniqueId({ length: 4 });
        const randomUUID = uId.randomUUID();

        dispatch(
          addCalculationField(propKey, randomUUID, {
            tableId: item.fieldData.tableId,
            fieldName: item.fieldData.fieldname,
            displayName: item.fieldData.displayname,
            dataType: item.fieldData.dataType,

            // TODO: This has to be checked, probably we don't need timeGrain in Number and String
            // timeGrain: 'year',
          }),
        );
        if (missingFields.length > 0) {
          const key = Object.keys(missingFields[0])[0];
          const field = missingFields[0][key];
          dispatch(
            replaceSourceInfoInCalculationFlow(
              randomUUID,
              {
                uid: activeFlow,
                //TODO: introduce step
                flow: currentCalculationSession.calculationInfo.flows[activeFlow][
                  activeCondition === null ? 0 : activeCondition
                ].flow,

                // TODO: introduce aggregation type here, like sum, avg, min, max etc
              },
              propKey,
              'field',
              false,
              allowAggregationFlowTypes.includes(activeFlowType) ? 'sum' : null,
              activeCondition === null ? 0 : activeCondition,
              field.index, // missing field index to replace with the new field sourceId
            ),
          );
          dispatch(deleteSourceField(key, propKey));
          getMissingFields();
        } else {
          dispatch(
            addSourceInfoInCalculationFlow(
              randomUUID,
              {
                uid: activeFlow,
                //TODO: introduce step
                flow: currentCalculationSession.calculationInfo.flows[activeFlow][
                  activeCondition === null ? 0 : activeCondition
                ].flow,

                // TODO: introduce aggregation type here, like sum, avg, min, max etc
              },
              propKey,
              'field',
              false,
              allowAggregationFlowTypes.includes(activeFlowType) ? 'sum' : null,
              activeCondition === null ? 0 : activeCondition,
            ),
          );
        }

        if (['log', 'absolute'].includes(activeFlowName))
          dispatch(setIsAggregated(propKey, activeCondition ?? 0, activeFlow, true));
      } else if (item.type === 'calculationSelected') {
        const maxOneSourceAllowedList = minMax[activeFlowType][activeFlowName].max;

        let flowResultantDatatype;

        if (currentCalculationSession.calculationInfo.flows[item.uid][0].flow === 'IfElse') {
          flowResultantDatatype =
            currentCalculationSession.calculationInfo.selectedResultType[item.uid];

          if (!flowResultantDatatype) return;
        } else {
          flowResultantDatatype =
            currentCalculationSession.calculationInfo.flows[item.uid][0]?.resultantDatatype;

          if (!flowResultantDatatype) return;
        }

        if (
          currentCalculationSession.calculationInfo.flows[activeFlow][0].sourceType.filter(
            (item: any) => item === 'field' || item === 'flow',
          ).length >= maxOneSourceAllowedList
        ) {
          setAlert({
            severity: 'warning',
            message: 'Can not add more than one source to this flow',
          });
          return;
        }

        if (
          activeFlowType === 'Number' &&
          !['decimal', 'integer'].includes(flowResultantDatatype.toLocaleLowerCase())
        ) {
          setAlert({
            severity: 'warning',
            message: 'Incorrect data type for calculation',
          });
          return;
        } else if (
          activeFlowType === 'String' &&
          !['string', 'text'].includes(flowResultantDatatype.toLocaleLowerCase())
        ) {
          setAlert({
            severity: 'warning',
            message: 'Incorrect data type for calculation',
          });
          return;
        }

        dispatch(insertFlowSource(propKey, activeFlow, item.uid));
        if (['log', 'absolute'].includes(activeFlowName))
          dispatch(
            setIsAggregated(propKey, activeCondition ?? 0, activeFlow, item.fieldData.isAggregated),
          );
      }
    },
  });

  useEffect(() => {
    if (!isOver) {
      setShowBorder(false);
    }
  }, [isOver]);

  if (activeFlowName === '') return null;
  return (
    <div
      ref={dropRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        padding: '5px',
        border: '1px solid transparent',
        // height: `${
        //   [
        //     'addition',
        //     'multiplication',
        //     'min',
        //     'max',
        //     'absolute',
        //     'ceiling',
        //     'floor',
        //     'division',
        //     'subtraction',
        //     'lowercase',
        //     'uppercase',
        //     'propercase',
        //     'trim',
        //     'length',
        //     'ltrim',
        //     'rtrim',
        //     'concat',
        //   ].includes(activeFlowName)
        //     ? '100%'
        //     : '30%'
        // }`,
        height: '100%',
        minHeight: '40px',
      }}
      className={`dropzone ${showBorder ? 'dropzone-highlighted' : ''}`}
    >
      {children}
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
    </div>
  );
}

export default StringNumberDropZone;
